import angular from 'angular';
import _ from 'lodash-es';
import moment from 'moment';
import kformUtils from '../utils/kform-utils';

/**
 * @typedef KformComponentControllerOptionsScope
 * @property {boolean} [builder]
 * @property {boolean} [building]
 */

/**
 * @typedef KformComponentControllerScope
 * @property {import('angular').IFormController} $parent
 * @property {Function} addFieldValue
 * @property {any} builder
 * @property {Function} checkConditional
 * @property {any} component
 * @property {string} componentId
 * @property {any} data
 * @property {any} form
 * @property {string} formName
 * @property {Function} getInputGroupStyles
 * @property {Function} getLabelStyles
 * @property {Function} getOptionInputStyles
 * @property {Function} getOptionLabelStyles
 * @property {number} gridCol
 * @property {number} gridRow
 * @property {any[]} hideComponents
 * @property {Function} invalidQuestions
 * @property {import('angular').IFormController} kformForm
 * @property {Function} isDisabled
 * @property {Function} isRequired
 * @property {Function} isVisible
 * @property {Function} labelVisible
 * @property {KformComponentControllerOptionsScope} options
 * @property {boolean} readOnly
 * @property {Function} removeFieldValue
 * @property {Function} resetForm
 * @property {any} submission
 * @property {string} template
 * @property {Function} topOrLeftOptionLabel
 */

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the left?
 */
const labelOnTheLeft = position => [
	'left-left',
	'left-right',
].includes(position);

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the right?
 */
const labelOnTheRight = position => [
	'right-left',
	'right-right',
].includes(position);

/**
 * @param {string} position Position
 * @returns {boolean} Is label on the left or right?
 */
const labelOnTheLeftOrRight = position => labelOnTheLeft(position) || labelOnTheRight(position);

/**
 * @param {string} position Position
 * @returns {string} Position
 */
const getLabelTextAlign = position => (position.match('-right') ? 'right' : 'left');

/**
 * @param {any} component Component
 * @returns {number} Label width
 */
const getComponentLabelWidth = component => {
	if (angular.isUndefined(component.labelWidth)) {
		component.labelWidth = 30;
	}

	return component.labelWidth;
};

/**
 * @param {any} component Component
 * @returns {number} Label margin
 */
const getComponentLabelMargin = component => {
	if (angular.isUndefined(component.labelMargin)) {
		component.labelMargin = 3;
	}

	return component.labelMargin;
};

const KformComponentController = /* @ngInject */ (
	/** @type {import('angular').IRootScopeService & KformComponentControllerScope} */ $scope,
	/** @type {import('angular').IControllerService} */ $controller,
	/** @type {any} */ KformComponents,
	/** @type {import('angular').ITimeoutService} */ $timeout,
) => {
	$scope.options = $scope.options || {};
	if ($scope.builder === undefined) {
		$scope.builder = $scope.options.builder === true;
	}

	$scope.kformForm = $scope.$parent[$scope.formName];

	// Options to match jquery.maskedinput masks
	// $scope.uiMaskOptions = {
	// 	maskDefinitions: {
	// 		9:   /\d/,
	// 		a:   /[A-Za-z]/,
	// 		'*': /[\dA-Za-z]/,
	// 	},
	// 	clearOnBlur: false,
	// 	eventsToHandle: ['input', 'keyup', 'click', 'focus'],
	// 	silentEvents: ['click', 'focus'],
	// };

	// Determine if the label should be visible.
	$scope.labelVisible = () => ($scope.component.inDataGrid
		? !!$scope.component.dataGridLabel
		: ('hideLabel' in $scope.component && $scope.component.label && !$scope.component.hideLabel));

	// See if this component is visible or not.
	$scope.isVisible = (/** @type {any} */ component, /** @type {any} */ row) => {
		if ($scope.options && $scope.options.building) {
			return true;
		}
		return kformUtils.isVisible(
			component,
			row,
			$scope.submission ? $scope.submission.data : null,
			$scope.hideComponents,
			$scope.form,
		);
	};

	// FOR-245 - Fix reset buttons.
	$scope.resetForm = () => {
		// Manually remove each key so we don't lose a reference to original
		// data in child scopes.
		for (const key in $scope.data) {
			if (Object.prototype.hasOwnProperty.call($scope.data, key)) {
				delete $scope.data[key];
			}
		}

		// Set the form to pristine again.
		for (const compKey in $scope.kformForm) {
			if ('$setPristine' in $scope.kformForm[compKey]) {
				$scope.kformForm[compKey].$setPristine();
				$scope.kformForm[compKey].$setUntouched();
			}
		}
	};

	$scope.isDisabled = (/** @type {any} */ component) => $scope.readOnly
		|| (typeof $scope.$parent.isDisabled === 'function' && $scope.$parent.isDisabled(component));

	$scope.isRequired = (/** @type {any} */ component) => kformUtils.isRequired(component);

	$scope.getLabelStyles = (/** @type {any} */ component) => {
		const labelPosition = _.get(component, 'labelPosition');

		if (labelOnTheLeft(labelPosition)) {
			return {
				float: 'left',
				width: `${getComponentLabelWidth(component) }%`,
				'margin-right': `${getComponentLabelMargin(component) }%`,
				'text-align': getLabelTextAlign(labelPosition),
			};
		}

		if (labelOnTheRight(labelPosition)) {
			return {
				float: 'right',
				width: `${getComponentLabelWidth(component) }%`,
				'margin-left': `${getComponentLabelMargin(component) }%`,
				'text-align': getLabelTextAlign(labelPosition),
			};
		}

		if (component.type === 'datagrid' && angular.isUndefined(labelPosition)) {
			return {
				display: 'block',
				'margin-bottom': '-10px',
			};
		}

		return undefined;
	};

	$scope.getInputGroupStyles = (/** @type {any} */ component) => {
		const labelPosition = _.get(component, 'labelPosition');

		if (labelOnTheLeftOrRight(labelPosition)) {
			const totalLabelWidth = getComponentLabelWidth(component) + getComponentLabelMargin(component);

			/** @type {any} */
			const styles = {
				width: `${100 - totalLabelWidth }%`,
			};

			if (labelOnTheLeft(labelPosition)) {
				styles['margin-left'] = `${totalLabelWidth }%`;
			}
			else {
				styles['margin-right'] = `${totalLabelWidth }%`;
			}

			return styles;
		}

		if (component.type === 'panel') {
			return { display: 'block' };
		}

		return undefined;
	};

	$scope.topOrLeftOptionLabel = kformUtils.optionsLabelPositionWrapper(kformUtils.topOrLeftOptionLabel);
	$scope.getOptionLabelStyles = kformUtils.optionsLabelPositionWrapper(kformUtils.getOptionLabelStyles);
	$scope.getOptionInputStyles = kformUtils.optionsLabelPositionWrapper(kformUtils.getOptionInputStyles);

	// Survey components haves questions.
	// We want to make the survey component label marked with error if any
	// of the questions is in invalid state.
	// So, first check if conponent has questions, then iterate over them.
	// Break in the first invalid question. Is enough to set the has-error
	// class to the survey component.
	// Note: Chek that this method is used in the template.
	$scope.invalidQuestions = (/** @type {any} */ kformForm) => {
		if (!kformForm) {
			return false;
		}

		let errorInQuestions = false;
		if ($scope.component.questions) {
			let index;
			for (index = 0; index < $scope.component.questions.length; index++) {
				const question = $scope.component.questions[index];
				const questionInputName = [$scope.component.key, question.value].join('-');
				const formInput = kformForm[questionInputName];
				if (formInput && !formInput.$pristine && formInput.$invalid) {
					errorInQuestions = true;
					break;
				}
			}
		}
		else {
			errorInQuestions = false;
		}
		return errorInQuestions;
	};

	// Pass through checkConditional since this is an isolate scope.
	$scope.checkConditional = $scope.$parent.checkConditional;
	// FOR-71 - Dont watch in the builder view.
	// Calculate value when data changes.
	if (!$scope.options.building && ($scope.component.calculateValue || _.get($scope.component, 'validate.json'))) {
		$scope.$watch('submission.data', () => {
			kformUtils.checkCalculated($scope.component, $scope.submission, $scope.data);

			// Process jsonLogic stuff if present.
			if (_.get($scope.component, 'validate.json')) {
				let input;

				// Only json parse once.
				if (typeof $scope.component.validate.json === 'string') {
					try {
						input = JSON.parse($scope.component.validate.json);
						$scope.component.validate.json = input;
					}
					catch {
						/* eslint-disable no-console */
						console.warn(`Invalid JSON validator given for ${ $scope.component.key}`);
						console.warn($scope.component.validate.json);
						/* eslint-enable no-console */
						delete $scope.component.validate.json;
						return;
					}
				}
				else {
					input = $scope.component.validate.json;
				}

				/**
				 * @type {boolean}
				 */
				let valid = true;
				try {
					if ($scope.component.type === 'datetime') {
						$scope.data[$scope.component.key] = moment($scope.data[$scope.component.key]).toISOString();
					}
					valid = kformUtils.jsonLogic.apply(input, {
						data: $scope.submission ? $scope.submission.data : $scope.data,
						row: $scope.data,
					});
				}
				catch (/** @type {any} */ error) {
					valid = error.message;
				}

				$timeout(() => {
					try {
						if (valid !== true) {
							$scope.component.customError = valid;
							$scope.kformForm[$scope.componentId].$setValidity('custom', false);
							return;
						}

						$scope.kformForm[$scope.componentId].$setValidity('custom', true);
					}
					catch {
						// Ignore any issues while editing the components.
					}
				});
			}
		}, true);
	}

	// Get the settings.
	const component = KformComponents.components[$scope.component.type] || KformComponents.components.custom;

	// Set the component with the defaults from the component settings.
	// Dont add the default key, so that components without keys will remain visible by default.
	for (const [key, value] of Object.entries(component.settings)) {
		if (!Object.prototype.hasOwnProperty.call($scope.component, key) && key !== 'key') {
			$scope.component[key] = angular.copy(value);
		}
	}

	// Add a new field value.
	$scope.addFieldValue = () => {
		/** @type {any} */
		const defaultData = {};
		kformUtils.checkDefaultValue($scope.component, $scope.submission, defaultData, $scope, () => {
			$scope.data[$scope.component.key] = $scope.data[$scope.component.key] || [];
			$scope.data[$scope.component.key].push(defaultData[$scope.component.key]);
		});
	};

	// Remove a field value.
	$scope.removeFieldValue = (/** @type {number} */ index) => {
		if (!Array.isArray($scope.data[$scope.component.key])) {
			$scope.data[$scope.component.key] = [];
		}
		$scope.data[$scope.component.key].splice(index, 1);
	};

	// Set the template for the component.
	$scope.template = typeof component.template === 'function' ? component.template($scope) : component.template;

	// Allow component keys to look like "settings[username]"
	if ($scope.component.key && $scope.component.key.includes('[')) {
		const matches = $scope.component.key.match(/([^[]+)\[([^]+)]/);
		if ((matches.length === 3) && Object.prototype.hasOwnProperty.call($scope.data, matches[1])) {
			$scope.data = $scope.data[matches[1]];
			$scope.component.key = matches[2];
		}
	}

	// If the component has a controller.
	if (component.controller) {
		// Maintain reverse compatibility by executing the old method style.
		if (typeof component.controller === 'function') {
			component.controller($scope.component, $scope);
		}
		else {
			$controller(component.controller, { $scope, $timeout });
		}
	}

	// FOR-71 - Dont watch in the builder view.
	if (!$scope.options.building) {
		$scope.$watch('component.validate.card', () => {
			$scope.data = $scope.data || {};
			kformUtils.checkDefaultValue($scope.component, $scope.submission, $scope.data, $scope);
		});
	}

	// Set the component name.
	$scope.componentId = $scope.component.key;
	if ($scope.gridRow !== undefined) {
		$scope.componentId += (`-${ $scope.gridRow}`);
	}
	if ($scope.gridCol !== undefined) {
		$scope.componentId += (`-${ $scope.gridCol}`);
	}
};

export default KformComponentController;
